import Form from 'antd/lib/form';
import * as React from 'react';
import { SettingField, SettingOption } from '../../types';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import { filterOptionLabel } from '../../helpers/formHelpers';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import { runwayColors } from '../styles/globalStyles';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { ReleaseMilestone, allReleaseMilestones, releaseMilestoneLabels } from '../../types/automation';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';

const TRANSITION_MILESTONE_SELECT_WIDTH = 240;

interface MilestoneStatusesRow {
    milestone?: ReleaseMilestone;
    statuses?: string[];
}

// Rows are kept locally, since one without a milestone yet has no key in the saved map
export const MilestoneStatusesInput: React.FC<{
    statusOptions: SettingOption[];
    kickoffField?: SettingField;
    value?: Partial<Record<ReleaseMilestone, string[]>>;
    onChange?: (value: Partial<Record<ReleaseMilestone, string[]>>) => void;
}> = ({ statusOptions, kickoffField, value, onChange }) => {
    const [rows, setRows] = React.useState<MilestoneStatusesRow[]>(() => {
        const savedRows = allReleaseMilestones
            .filter((milestone) => value?.[milestone])
            .map((milestone) => ({ milestone, statuses: value?.[milestone] }));
        return savedRows.length ? savedRows : [{ milestone: ReleaseMilestone.ReleaseCompleted }];
    });

    // A milestone without statuses is still sent, so the backend asks for them instead of silently dropping it
    const updateRows = (updatedRows: MilestoneStatusesRow[]) => {
        setRows(updatedRows);
        const statusesByMilestone: Partial<Record<ReleaseMilestone, string[]>> = {};
        for (const row of updatedRows) {
            if (row.milestone) {
                statusesByMilestone[row.milestone] = row.statuses ?? [];
            }
        }
        onChange?.(statusesByMilestone);
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {rows.map((row, index) => (
                <Space key={index} direction="vertical" size={8} style={{ width: '100%' }}>
                    <Row gutter={8} align="top" wrap={false} style={{ width: '100%' }}>
                        <Col flex="none">
                            <div style={{ lineHeight: '32px', color: runwayColors.darkTextGrey }}>On</div>
                        </Col>
                        <Col flex={`0 0 ${TRANSITION_MILESTONE_SELECT_WIDTH}px`}>
                            <Select
                                placeholder="Select milestone"
                                popupMatchSelectWidth={false}
                                style={{ width: '100%' }}
                                value={row.milestone}
                                onChange={(milestone: ReleaseMilestone) =>
                                    updateRows(
                                        rows.map((otherRow, otherIndex) =>
                                            otherIndex === index ? { ...otherRow, milestone } : otherRow,
                                        ),
                                    )
                                }
                            >
                                {allReleaseMilestones.map((milestone) => (
                                    <Select.Option
                                        value={milestone}
                                        key={milestone}
                                        disabled={rows.some(
                                            (otherRow, otherIndex) =>
                                                otherIndex !== index && otherRow.milestone === milestone,
                                        )}
                                    >
                                        {releaseMilestoneLabels[milestone]}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col flex="none">
                            <div style={{ lineHeight: '32px', color: runwayColors.darkTextGrey, whiteSpace: 'nowrap' }}>
                                update to
                            </div>
                        </Col>
                        <Col flex="auto" style={{ minWidth: 0 }}>
                            <Select
                                mode="multiple"
                                placeholder="Select statuses"
                                filterOption={filterOptionLabel}
                                style={{ width: '100%' }}
                                showSearch
                                value={row.statuses}
                                onChange={(statuses: string[]) =>
                                    updateRows(
                                        rows.map((otherRow, otherIndex) =>
                                            otherIndex === index ? { ...otherRow, statuses } : otherRow,
                                        ),
                                    )
                                }
                                options={statusOptions.map((option) => ({
                                    value: option.value,
                                    label: option.label,
                                }))}
                            />
                        </Col>
                        <Col flex="none">
                            <Button
                                icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                danger
                                type="link"
                                onClick={() => updateRows(rows.filter((_, otherIndex) => otherIndex !== index))}
                            />
                        </Col>
                    </Row>
                    {row.milestone === ReleaseMilestone.Kickoff && kickoffField && (
                        <Form.Item name={kickoffField.key} valuePropName="checked" style={{ marginBottom: 0 }}>
                            <Checkbox>{kickoffField.label}</Checkbox>
                        </Form.Item>
                    )}
                </Space>
            ))}
            {rows.length < allReleaseMilestones.length && (
                <Button
                    icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                    onClick={() => setRows([...rows, {}])}
                >
                    Add another status update
                </Button>
            )}
        </Space>
    );
};
